import { Vec2 } from '@mine-or-die/shared';

/**
 * Particle for visual effects
 */
export interface Particle {
  pos: Vec2;
  vel: Vec2;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  type: 'spark' | 'debris' | 'trail' | 'ring' | 'line' | 'fractal';
  rotation?: number;
  rotationSpeed?: number;
  // For line particles (ship death effect)
  endOffset?: Vec2;
}

/**
 * Screen shake state
 */
export interface ScreenShake {
  intensity: number;
  duration: number;
  elapsed: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Score popup floating text
 */
export interface ScorePopup {
  pos: Vec2;
  text: string;
  color: string;
  life: number;
  maxLife: number;
  vel: Vec2;
}

/**
 * Effects manager - handles particles, screen shake, and popups
 */
export class EffectsManager {
  private particles: Particle[] = [];
  private shake: ScreenShake = { intensity: 0, duration: 0, elapsed: 0, offsetX: 0, offsetY: 0 };
  private popups: ScorePopup[] = [];

  // Particle pool for performance
  private readonly maxParticles = 500;

  /**
   * Update all effects
   */
  update(dt: number): void {
    this.updateParticles(dt);
    this.updateShake(dt);
    this.updatePopups(dt);
  }

  /**
   * Update particles
   */
  private updateParticles(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];

      // Update position
      p.pos = p.pos.add(p.vel.scale(dt));

      // Apply drag to velocity
      p.vel = p.vel.scale(0.98);

      // Update rotation
      if (p.rotation !== undefined && p.rotationSpeed) {
        p.rotation += p.rotationSpeed * dt;
      }

      // Update life
      p.life -= dt;

      // Remove dead particles
      if (p.life <= 0) {
        this.particles.splice(i, 1);
      }
    }
  }

  /**
   * Update screen shake
   */
  private updateShake(dt: number): void {
    if (this.shake.duration <= 0) {
      this.shake.offsetX = 0;
      this.shake.offsetY = 0;
      return;
    }

    this.shake.elapsed += dt;

    if (this.shake.elapsed >= this.shake.duration) {
      this.shake.duration = 0;
      this.shake.offsetX = 0;
      this.shake.offsetY = 0;
      return;
    }

    // Calculate shake with decay
    const progress = this.shake.elapsed / this.shake.duration;
    const decay = 1 - progress;
    const intensity = this.shake.intensity * decay;

    this.shake.offsetX = (Math.random() - 0.5) * 2 * intensity;
    this.shake.offsetY = (Math.random() - 0.5) * 2 * intensity;
  }

  /**
   * Update score popups
   */
  private updatePopups(dt: number): void {
    for (let i = this.popups.length - 1; i >= 0; i--) {
      const popup = this.popups[i];

      popup.pos = popup.pos.add(popup.vel.scale(dt));
      popup.life -= dt;

      if (popup.life <= 0) {
        this.popups.splice(i, 1);
      }
    }
  }

  /**
   * Get screen shake offset
   */
  getShakeOffset(): { x: number; y: number } {
    return { x: this.shake.offsetX, y: this.shake.offsetY };
  }

  /**
   * Trigger screen shake
   */
  addScreenShake(intensity: number, duration: number): void {
    // Stack shakes by taking max
    if (intensity > this.shake.intensity || this.shake.duration <= 0) {
      this.shake.intensity = intensity;
      this.shake.duration = duration;
      this.shake.elapsed = 0;
    }
  }

  /**
   * Create explosion effect at position
   */
  spawnExplosion(pos: Vec2, color: string, size: number = 1): void {
    const particleCount = Math.floor(12 * size);

    // Spark particles
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2 + Math.random() * 0.5;
      const speed = 100 + Math.random() * 200 * size;
      const vel = Vec2.fromAngle(angle).scale(speed);

      this.addParticle({
        pos: pos.clone(),
        vel,
        life: 0.3 + Math.random() * 0.4,
        maxLife: 0.7,
        size: 2 + Math.random() * 3,
        color,
        type: 'spark',
      });
    }

    // Debris particles
    for (let i = 0; i < particleCount / 2; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 50 + Math.random() * 100 * size;
      const vel = Vec2.fromAngle(angle).scale(speed);

      this.addParticle({
        pos: pos.add(Vec2.random().scale(10)),
        vel,
        life: 0.5 + Math.random() * 0.5,
        maxLife: 1,
        size: 4 + Math.random() * 6,
        color,
        type: 'debris',
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 10,
      });
    }

    // Expanding ring
    this.addParticle({
      pos: pos.clone(),
      vel: Vec2.zero(),
      life: 0.3,
      maxLife: 0.3,
      size: 10 * size,
      color,
      type: 'ring',
    });

    // Screen shake
    this.addScreenShake(8 * size, 0.2);
  }

  /**
   * Create hit spark effect
   */
  spawnHitSparks(pos: Vec2, color: string, direction?: Vec2): void {
    const baseAngle = direction ? direction.angle() : Math.random() * Math.PI * 2;

    for (let i = 0; i < 6; i++) {
      const angle = baseAngle + (Math.random() - 0.5) * Math.PI * 0.8;
      const speed = 80 + Math.random() * 120;
      const vel = Vec2.fromAngle(angle).scale(speed);

      this.addParticle({
        pos: pos.clone(),
        vel,
        life: 0.15 + Math.random() * 0.15,
        maxLife: 0.3,
        size: 2 + Math.random() * 2,
        color,
        type: 'spark',
      });
    }

    this.addScreenShake(3, 0.1);
  }

  /**
   * Create crate pickup effect
   */
  spawnPickupEffect(pos: Vec2, color: string): void {
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const vel = Vec2.fromAngle(angle).scale(60);

      this.addParticle({
        pos: pos.clone(),
        vel,
        life: 0.2,
        maxLife: 0.2,
        size: 3,
        color,
        type: 'spark',
      });
    }
  }

  /**
   * Create turn-in celebration effect
   */
  spawnTurnInEffect(pos: Vec2, color: string, crateCount: number): void {
    const particleCount = Math.min(crateCount * 4, 30);

    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 100 + Math.random() * 150;
      const vel = Vec2.fromAngle(angle).scale(speed);

      this.addParticle({
        pos: pos.add(Vec2.random().scale(20)),
        vel,
        life: 0.4 + Math.random() * 0.4,
        maxLife: 0.8,
        size: 3 + Math.random() * 4,
        color,
        type: 'spark',
      });
    }

    // Multiple rings
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        this.addParticle({
          pos: pos.clone(),
          vel: Vec2.zero(),
          life: 0.4,
          maxLife: 0.4,
          size: 20 + i * 15,
          color,
          type: 'ring',
        });
      }, i * 100);
    }

    this.addScreenShake(5, 0.3);
  }

  /**
   * Create ship death explosion - lines fly off in all directions and dissipate
   * This is the digital explosion effect when a ship is destroyed
   */
  spawnShipDeathExplosion(pos: Vec2, color: string, shipVertices: Vec2[]): void {
    // Create line particles from each edge of the ship
    for (let i = 0; i < shipVertices.length; i++) {
      const start = shipVertices[i];
      const end = shipVertices[(i + 1) % shipVertices.length];

      // Line flies off in direction perpendicular to its center, plus some randomness
      const midpoint = start.add(end).scale(0.5);
      const awayFromCenter = midpoint.sub(pos).normalize();
      const angle = awayFromCenter.angle() + (Math.random() - 0.5) * 0.8;
      const speed = 150 + Math.random() * 200;
      const vel = Vec2.fromAngle(angle).scale(speed);

      // The line particle stores relative offset from start to end
      this.addParticle({
        pos: start.clone(),
        vel,
        life: 0.8 + Math.random() * 0.4,
        maxLife: 1.2,
        size: 2,
        color,
        type: 'line',
        endOffset: end.sub(start),
        rotation: 0,
        rotationSpeed: (Math.random() - 0.5) * 8,
      });
    }

    // Add spark burst at center
    for (let i = 0; i < 20; i++) {
      const angle = (i / 20) * Math.PI * 2 + Math.random() * 0.3;
      const speed = 80 + Math.random() * 180;
      const vel = Vec2.fromAngle(angle).scale(speed);

      this.addParticle({
        pos: pos.clone(),
        vel,
        life: 0.3 + Math.random() * 0.3,
        maxLife: 0.6,
        size: 2 + Math.random() * 3,
        color,
        type: 'spark',
      });
    }

    // Expanding rings
    for (let i = 0; i < 2; i++) {
      this.addParticle({
        pos: pos.clone(),
        vel: Vec2.zero(),
        life: 0.4,
        maxLife: 0.4,
        size: 15 + i * 20,
        color,
        type: 'ring',
      });
    }

    this.addScreenShake(12, 0.3);
  }

  /**
   * Create respawn flash effect - bright digital fractals
   */
  spawnRespawnFlash(pos: Vec2, color: string): void {
    // Fractal/crystalline pattern - radial lines
    const numLines = 12;
    for (let i = 0; i < numLines; i++) {
      const angle = (i / numLines) * Math.PI * 2;
      const length = 30 + Math.random() * 40;
      const endOffset = Vec2.fromAngle(angle).scale(length);

      // Lines expand outward from center
      this.addParticle({
        pos: pos.clone(),
        vel: Vec2.fromAngle(angle).scale(100),
        life: 0.4,
        maxLife: 0.4,
        size: 2,
        color,
        type: 'fractal',
        endOffset,
        rotation: angle,
        rotationSpeed: 0,
      });
    }

    // Secondary fractal branches
    for (let i = 0; i < numLines * 2; i++) {
      const angle = (i / (numLines * 2)) * Math.PI * 2 + Math.PI / numLines;
      const length = 15 + Math.random() * 25;
      const endOffset = Vec2.fromAngle(angle).scale(length);
      const startOffset = Vec2.fromAngle(angle).scale(10);

      this.addParticle({
        pos: pos.add(startOffset),
        vel: Vec2.fromAngle(angle).scale(60),
        life: 0.35,
        maxLife: 0.35,
        size: 1.5,
        color,
        type: 'fractal',
        endOffset,
        rotation: angle,
        rotationSpeed: 0,
      });
    }

    // Bright center flash (large spark)
    this.addParticle({
      pos: pos.clone(),
      vel: Vec2.zero(),
      life: 0.3,
      maxLife: 0.3,
      size: 40,
      color,
      type: 'spark',
    });

    // Ring expanding
    this.addParticle({
      pos: pos.clone(),
      vel: Vec2.zero(),
      life: 0.5,
      maxLife: 0.5,
      size: 25,
      color,
      type: 'ring',
    });

    this.addScreenShake(5, 0.15);
  }

  /**
   * Create engine trail particle
   */
  spawnEngineTrail(pos: Vec2, shipVel: Vec2, color: string): void {
    if (this.particles.length > this.maxParticles * 0.8) return; // Don't spam trails

    const vel = shipVel.scale(-0.3).add(Vec2.random().scale(20));

    this.addParticle({
      pos: pos.add(Vec2.random().scale(3)),
      vel,
      life: 0.15 + Math.random() * 0.1,
      maxLife: 0.25,
      size: 2 + Math.random() * 2,
      color: color + '80',
      type: 'trail',
    });
  }

  /**
   * Add score popup
   */
  addScorePopup(pos: Vec2, text: string, color: string): void {
    this.popups.push({
      pos: pos.clone(),
      text,
      color,
      life: 1,
      maxLife: 1,
      vel: new Vec2(0, -50),
    });
  }

  /**
   * Add a particle
   */
  private addParticle(particle: Particle): void {
    if (this.particles.length >= this.maxParticles) {
      // Remove oldest particle
      this.particles.shift();
    }
    this.particles.push(particle);
  }

  /**
   * Get all particles for rendering
   */
  getParticles(): readonly Particle[] {
    return this.particles;
  }

  /**
   * Get all popups for rendering
   */
  getPopups(): readonly ScorePopup[] {
    return this.popups;
  }

  /**
   * Clear all effects
   */
  clear(): void {
    this.particles = [];
    this.popups = [];
    this.shake = { intensity: 0, duration: 0, elapsed: 0, offsetX: 0, offsetY: 0 };
  }
}
